import React, { useState } from "react";
import { useAuth } from "../../context/AuthContext";

type AuthMode = "signin" | "signup";
type ViewMode = "auth" | "reset-request" | "reset-verify";

type FormState = {
  name: string;
  email: string;
  password: string;
  confirmPassword: string;
};

const emptyForm: FormState = {
  name: "",
  email: "",
  password: "",
  confirmPassword: "",
};

const AuthScreen: React.FC = () => {
  const { signIn, signUp, requestPasswordReset, confirmPasswordReset } =
    useAuth();
  const [mode, setMode] = useState<AuthMode>("signin");
  const [view, setView] = useState<ViewMode>("auth");
  const [formState, setFormState] = useState<FormState>(emptyForm);
  const [error, setError] = useState<string | null>(null);
  const [infoMessage, setInfoMessage] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [resetEmail, setResetEmail] = useState("");
  const [resetOtp, setResetOtp] = useState("");
  const [resetPassword, setResetPassword] = useState("");
  const [resetConfirmPassword, setResetConfirmPassword] = useState("");
  const [resetContextEmail, setResetContextEmail] = useState("");
  const [resetError, setResetError] = useState<string | null>(null);
  const [resetMessage, setResetMessage] = useState<string | null>(null);
  const [resetSubmitting, setResetSubmitting] = useState(false);

  const resetResetFlow = () => {
    setResetEmail("");
    setResetOtp("");
    setResetPassword("");
    setResetConfirmPassword("");
    setResetContextEmail("");
    setResetError(null);
    setResetMessage(null);
    setResetSubmitting(false);
  };

  const handleBackToAuth = () => {
    resetResetFlow();
    setView("auth");
    setMode("signin");
  };

  const handleModeToggle = (nextMode: AuthMode) => {
    setError(null);
    setInfoMessage(null);
    setIsSubmitting(false);
    setFormState(emptyForm);
    setRememberMe(false);
    resetResetFlow();
    setView("auth");
    setMode(nextMode);
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const { name, value } = event.target;
    setFormState((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError(null);
    setIsSubmitting(true);

    try {
      if (mode === "signup") {
        if (formState.password !== formState.confirmPassword) {
          throw new Error("Passwords do not match.");
        }
        await signUp({
          name: formState.name,
          email: formState.email,
          password: formState.password,
          rememberMe,
        });
      } else {
        await signIn({
          email: formState.email,
          password: formState.password,
          rememberMe,
        });
      }
      setFormState(emptyForm);
      setRememberMe(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Something went wrong.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleStartReset = () => {
    setError(null);
    setInfoMessage(null);
    setResetError(null);
    setResetMessage(null);
    setResetEmail(formState.email || "");
    setView("reset-request");
  };

  const handleResetRequest = async (
    event: React.FormEvent<HTMLFormElement>
  ) => {
    event.preventDefault();
    setResetError(null);
    setResetMessage(null);

    const normalizedEmail = resetEmail.trim().toLowerCase();
    if (!normalizedEmail) {
      setResetError("Email is required.");
      return;
    }

    setResetSubmitting(true);
    try {
      await requestPasswordReset(normalizedEmail);
      setResetContextEmail(normalizedEmail);
      setResetOtp("");
      setResetPassword("");
      setResetConfirmPassword("");
      setResetMessage(
        "We sent a 6-digit code to your email. Enter it below to continue."
      );
      setView("reset-verify");
    } catch (err) {
      setResetError(
        err instanceof Error ? err.message : "Unable to send reset code."
      );
    } finally {
      setResetSubmitting(false);
    }
  };

  const handleResetConfirm = async (
    event: React.FormEvent<HTMLFormElement>
  ) => {
    event.preventDefault();
    setResetError(null);
    setResetMessage(null);

    const otpValue = resetOtp.trim();
    const passwordValue = resetPassword.trim();
    const confirmValue = resetConfirmPassword.trim();
    const emailValue = resetContextEmail || resetEmail.trim().toLowerCase();

    if (!emailValue || !otpValue || passwordValue.length < 6) {
      setResetError(
        "Email, OTP, and a new password (min 6 characters) are required."
      );
      return;
    }

    if (passwordValue !== confirmValue) {
      setResetError("Passwords do not match.");
      return;
    }

    setResetSubmitting(true);
    try {
      await confirmPasswordReset({
        email: emailValue,
        otp: otpValue,
        password: passwordValue,
      });
      setInfoMessage("Password reset successful. Please sign in again.");
      setFormState((prev) => ({
        ...prev,
        email: emailValue,
        password: "",
      }));
      handleBackToAuth();
    } catch (err) {
      setResetError(
        err instanceof Error ? err.message : "Unable to reset password."
      );
    } finally {
      setResetSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen w-full bg-[#1F1F1F] flex items-center justify-center px-6 py-10 text-[#E0E0E0]">
      <div className="w-full max-w-md bg-[#2A2A2A]   rounded-xl shadow-lg p-8">
        <h1 className="text-2xl font-semibold text-center mb-2">
          Salesforce Indesign Connector
        </h1>
        <p className="text-sm text-center text-[#B0B0B0] mb-6">
          {mode === "signin"
            ? "Sign in to access."
            : "Create an account to get started."}
        </p>

        {view === "auth" ? (
          <>
            <div className="flex mb-6">
              <button
                type="button"
                onClick={() => handleModeToggle("signin")}
                className={`flex-1 py-2 rounded-l-md  ${
                  mode === "signin"
                    ? "bg-[#3F3F3F] text-white"
                    : "bg-[#262626] text-[#B0B0B0]"
                }`}
              >
                Sign In
              </button>
              <button
                type="button"
                onClick={() => handleModeToggle("signup")}
                className={`flex-1 py-2 rounded-r-md  -ml-px ${
                  mode === "signup"
                    ? "bg-[#3F3F3F] text-white"
                    : "bg-[#262626] text-[#B0B0B0]"
                }`}
              >
                Create Account
              </button>
            </div>

            <form className="space-y-4" onSubmit={handleSubmit}>
              {mode === "signup" && (
                <div>
                  <label
                    htmlFor="name"
                    className="block text-sm font-medium text-[#CFCFCF]"
                  >
                    Full Name
                  </label>
                  <input
                    id="name"
                    name="name"
                    type="text"
                    value={formState.name}
                    onChange={handleChange}
                    className="mt-1 block w-full rounded-md bg-[#1F1F1F]  px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#5A5A5A]"
                    placeholder="Alex Doe"
                    autoComplete="name"
                    required
                  />
                </div>
              )}

              <div>
                <label
                  htmlFor="email"
                  className="block text-sm font-medium text-[#CFCFCF]"
                >
                  Email
                </label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  value={formState.email}
                  onChange={handleChange}
                  className="mt-1 block w-full rounded-md bg-[#1F1F1F]  px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#5A5A5A]"
                  placeholder="alex@email.com"
                  autoComplete="email"
                  required
                />
              </div>

              <div>
                <label
                  htmlFor="password"
                  className="block text-sm font-medium text-[#CFCFCF]"
                >
                  Password
                </label>
                <input
                  id="password"
                  name="password"
                  type="password"
                  value={formState.password}
                  onChange={handleChange}
                  className="mt-1 block w-full rounded-md bg-[#1F1F1F]  px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#5A5A5A]"
                  placeholder="••••••••"
                  autoComplete={
                    mode === "signin" ? "current-password" : "new-password"
                  }
                  minLength={6}
                  required
                />
              </div>

              {mode === "signup" && (
                <div>
                  <label
                    htmlFor="confirmPassword"
                    className="block text-sm font-medium text-[#CFCFCF]"
                  >
                    Confirm Password
                  </label>
                  <input
                    id="confirmPassword"
                    name="confirmPassword"
                    type="password"
                    value={formState.confirmPassword}
                    onChange={handleChange}
                    className="mt-1 block w-full rounded-md bg-[#1F1F1F]  px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#5A5A5A]"
                    placeholder="••••••••"
                    autoComplete="new-password"
                    minLength={6}
                    required
                  />
                </div>
              )}

              <div className="flex items-center justify-between text-sm text-[#CFCFCF]">
                <label className="inline-flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={rememberMe}
                    onChange={(event) => setRememberMe(event.target.checked)}
                    className="h-4 w-4 rounded  bg-[#1F1F1F] text-[#C58C34] focus:ring-[#C58C34]"
                  />
                  Remember me
                </label>
                <button
                  type="button"
                  onClick={handleStartReset}
                  className="text-xs text-[#C58C34] hover:text-[#DAA14B]"
                >
                  Forgot password?
                </button>
              </div>

              {infoMessage && (
                <p className="text-sm text-green-300 bg-[#1f3722] ring-1 ring-inset ring-[#2e5c32] rounded-md px-3 py-2">
                  {infoMessage}
                </p>
              )}

              {error && (
                <p className="text-sm text-red-400 bg-[#3A1F1F] ring-1 ring-inset ring-[#5A2A2A] rounded-md px-3 py-2">
                  {error}
                </p>
              )}

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-[#C58C34] hover:bg-[#DAA14B] text-black font-semibold py-2 rounded-md transition-colors disabled:opacity-70 disabled:cursor-not-allowed"
              >
                {isSubmitting
                  ? "Please wait..."
                  : mode === "signin"
                  ? "Sign In"
                  : "Create Account"}
              </button>
            </form>
          </>
        ) : null}

        {view === "reset-request" && (
          <form className="space-y-4" onSubmit={handleResetRequest}>
            <p className="text-sm text-[#B0B0B0]">
              Enter the email linked to your account. We&apos;ll send a 6-digit
              verification code to reset your password.
            </p>
            <div>
              <label
                htmlFor="reset-email"
                className="block text-sm font-medium text-[#CFCFCF]"
              >
                Email
              </label>
              <input
                id="reset-email"
                name="resetEmail"
                type="email"
                value={resetEmail}
                onChange={(event) => setResetEmail(event.target.value)}
                className="mt-1 block w-full rounded-md bg-[#1F1F1F]  px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#5A5A5A]"
                placeholder="alex@email.com"
                autoComplete="email"
                required
              />
            </div>

            {resetError && (
              <p className="text-sm text-red-400 bg-[#3A1F1F] ring-1 ring-inset ring-[#5A2A2A] rounded-md px-3 py-2">
                {resetError}
              </p>
            )}

            <button
              type="submit"
              disabled={resetSubmitting}
              className="w-full bg-[#C58C34] hover:bg-[#DAA14B] text-black font-semibold py-2 rounded-md transition-colors disabled:opacity-70 disabled:cursor-not-allowed"
            >
              {resetSubmitting ? "Sending..." : "Send Reset Code"}
            </button>

            <button
              type="button"
              onClick={handleBackToAuth}
              className="w-full text-sm text-[#C58C34] hover:text-[#DAA14B] underline"
            >
              Back to Sign In
            </button>
          </form>
        )}

        {view === "reset-verify" && (
          <form className="space-y-4" onSubmit={handleResetConfirm}>
            <p className="text-sm text-[#B0B0B0]">
              Enter the code from your email and choose a new password.
            </p>
            <div>
              <label
                htmlFor="reset-email-confirm"
                className="block text-sm font-medium text-[#CFCFCF]"
              >
                Email
              </label>
              <input
                id="reset-email-confirm"
                type="email"
                value={resetContextEmail}
                readOnly
                className="mt-1 block w-full rounded-md bg-[#1F1F1F]  px-3 py-2 text-sm text-[#B0B0B0]"
              />
            </div>

            <div>
              <label
                htmlFor="reset-otp"
                className="block text-sm font-medium text-[#CFCFCF]"
              >
                Verification Code
              </label>
              <input
                id="reset-otp"
                type="text"
                value={resetOtp}
                onChange={(event) => setResetOtp(event.target.value)}
                className="mt-1 block w-full rounded-md bg-[#1F1F1F]  px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#5A5A5A]"
                placeholder="123456"
                inputMode="numeric"
                pattern="\d*"
                maxLength={6}
                required
              />
            </div>

            <div>
              <label
                htmlFor="reset-password"
                className="block text-sm font-medium text-[#CFCFCF]"
              >
                New Password
              </label>
              <input
                id="reset-password"
                type="password"
                value={resetPassword}
                onChange={(event) => setResetPassword(event.target.value)}
                className="mt-1 block w-full rounded-md bg-[#1F1F1F]  px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#5A5A5A]"
                placeholder="••••••••"
                minLength={6}
                autoComplete="new-password"
                required
              />
            </div>

            <div>
              <label
                htmlFor="reset-confirm-password"
                className="block text-sm font-medium text-[#CFCFCF]"
              >
                Confirm New Password
              </label>
              <input
                id="reset-confirm-password"
                type="password"
                value={resetConfirmPassword}
                onChange={(event) =>
                  setResetConfirmPassword(event.target.value)
                }
                className="mt-1 block w-full rounded-md bg-[#1F1F1F]  px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#5A5A5A]"
                placeholder="••••••••"
                minLength={6}
                autoComplete="new-password"
                required
              />
            </div>

            {resetMessage && (
              <p className="text-sm text-green-300 bg-[#1f3722] ring-1 ring-inset ring-[#2e5c32] rounded-md px-3 py-2">
                {resetMessage}
              </p>
            )}

            {resetError && (
              <p className="text-sm text-red-400 bg-[#3A1F1F] ring-1 ring-inset ring-[#5A2A2A] rounded-md px-3 py-2">
                {resetError}
              </p>
            )}

            <button
              type="submit"
              disabled={resetSubmitting}
              className="w-full bg-[#C58C34] hover:bg-[#DAA14B] text-black font-semibold py-2 rounded-md transition-colors disabled:opacity-70 disabled:cursor-not-allowed"
            >
              {resetSubmitting ? "Verifying..." : "Verify & Reset Password"}
            </button>

            <div className="flex items-center justify-between text-xs text-[#B0B0B0]">
              <button
                type="button"
                onClick={() => {
                  setResetMessage(null);
                  setView("reset-request");
                }}
                className="text-[#C58C34] hover:text-[#DAA14B]"
              >
                Use a different email
              </button>
              <button
                type="button"
                onClick={handleBackToAuth}
                className="text-[#C58C34] hover:text-[#DAA14B]"
              >
                Back to Sign In
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default AuthScreen;
